import { components } from "@appretio/api";
import { useRetainedState } from "@appretio/shared";
import { formatYearWeek } from "@appretio/shared";
import { useCallback, useMemo } from "react";
import type { AlgorithmicForecastLoad } from "../../components/algorithmicForecastDialog/algorithmicForecastLoad";
import type { ForecastTableControlValues } from "../../components/tableControls/forecastTableControlValues";
import { formatFiscalWeek } from "../../utils/formatFiscalWeek";
import { type CubePage, pageScope } from "../useCubeSelectionState";

type KpiDiagnostics = components["schemas"]["KpiDiagnostics"];
type FiscalWeek = components["schemas"]["FiscalWeek"];

/**
 * An algorithmic row as the grid holds it: the response row plus what the
 * producer cell shows — its KPI's diagnostics (joined on `metric_name` within
 * its own response), the load's ground-truth label and the fitted span.
 */
export type AlgoRow = components["schemas"]["FiscalGridData"] &
  Partial<
    Pick<
      KpiDiagnostics,
      | "weeks_available"
      | "weeks_nonzero"
      | "holdout_wape"
      | "holdout_weeks"
      | "holdout_unscored"
    >
  > & {
    /** "Actuals", or the other planner's cube label. */
    algo_ground_truth: string;
    /** `train_from – train_to`, spelled per the week-label toggle. */
    algo_training_range?: string;
  };

/** Return shape of {@link useAlgorithmicForecastState}. */
export interface AlgorithmicForecastState {
  /** Every held load's grid rows. */
  algoRows: AlgoRow[];
  /** Every held load's per-KPI failures; empty when nothing is held. */
  algoFailures: string[];
  /**
   * Holds a generate's per-model loads beside the ones already held (the load
   * dialog's `onLoaded`). A model's new load replaces its earlier one.
   */
  addAlgoForecast: (loads: AlgorithmicForecastLoad[]) => void;
  /** Drops every held load (Apply, version delete). */
  clearAlgoForecast: () => void;
}

/**
 * Fiscal labels come from the response's calendar-read fiscal address, not
 * from the ISO key: an ISO week cannot be turned into a fiscal one by
 * arithmetic. Calendar labels are the ISO key itself.
 */
function formatWeekRef(
  ref: FiscalWeek,
  weekLabels: ForecastTableControlValues["weekLabels"]
): string {
  return weekLabels === "Fiscal"
    ? formatFiscalWeek(ref.fiscal_year, ref.week_of_year)
    : formatYearWeek(ref.year_week);
}

function formatTrainingRange(
  diagnostics: KpiDiagnostics | undefined,
  weekLabels: ForecastTableControlValues["weekLabels"]
): string | undefined {
  const from = diagnostics?.train_from;
  const to = diagnostics?.train_to;
  if (!from || !to) return undefined;
  return `${formatWeekRef(from, weekLabels)} – ${formatWeekRef(to, weekLabels)}`;
}

/**
 * Holds the algorithmic-forecast loads as page state, separate from
 * `forecastData`, and denormalizes their diagnostics onto the grid rows.
 * Loads accumulate so models from separate loads sit side by side; a model's
 * new load replaces its earlier one, since the grid holds one row per
 * (KPI × fiscal year × producer).
 *
 * Retained in the page's scope like the grid data the loads sit beside, so
 * the grid a navigation brings back still carries them; the fetch that
 * replaces that data drops them (``useRetainedForecastData``).
 */
export function useAlgorithmicForecastState(
  page: CubePage,
  weekLabels: ForecastTableControlValues["weekLabels"]
): AlgorithmicForecastState {
  const [loads, setLoads] = useRetainedState<AlgorithmicForecastLoad[]>(
    pageScope(page),
    "algoLoads",
    []
  );

  const addAlgoForecast = useCallback(
    (next: AlgorithmicForecastLoad[]) => {
      setLoads((held) => {
        const replaced = new Set(next.map((load) => load.output.producer_type));
        return [
          ...held.filter((load) => !replaced.has(load.output.producer_type)),
          ...next
        ];
      });
    },
    [setLoads]
  );
  const clearAlgoForecast = useCallback(() => setLoads([]), [setLoads]);

  const algoRows = useMemo(
    (): AlgoRow[] =>
      loads.flatMap((load) => {
        const diagnosticsByMetric = new Map<string, KpiDiagnostics>(
          (load.output.diagnostics ?? []).map((d) => [d.metric_name, d])
        );
        return (load.output.fiscal_grid_data ?? []).map((row) => {
          const diagnostics = diagnosticsByMetric.get(row.metric_name);
          return {
            ...row,
            weeks_available: diagnostics?.weeks_available,
            weeks_nonzero: diagnostics?.weeks_nonzero,
            holdout_wape: diagnostics?.holdout_wape,
            holdout_weeks: diagnostics?.holdout_weeks,
            holdout_unscored: diagnostics?.holdout_unscored,
            algo_ground_truth: load.groundTruthLabel,
            algo_training_range: formatTrainingRange(diagnostics, weekLabels)
          };
        });
      }),
    [loads, weekLabels]
  );
  const algoFailures = useMemo(
    () => loads.flatMap((load) => load.failures),
    [loads]
  );

  return { algoRows, algoFailures, addAlgoForecast, clearAlgoForecast };
}
